import React, { useCallback, useEffect, useState } from "react";
import TablePaginationFooter from "../../../Components/Atoms/Table/TablePaginationFooter";
import LoadingPage from "../../LoadingPage";
import Table, {
  Column,
  DataSourceItem,
  RowSelectionActionOn
} from "../../../Components/Atoms/Table";
import Tag from "../../../Components/Atoms/Tag";
import { useDebounce } from "../../../Utils/ActionUtils";
import { useAuth0 } from "@auth0/auth0-react";
import { useCustomerStore } from "../../../Store/Customer";
import { useTeamStore } from "../../../Store/Team";
import { PAGINATION_KEYS, usePagination } from "../../../Store/Pagination";
import { Customer } from "../../../Contracts/Customer";
import { PaymentsIntegrationEnum } from "../../../Contracts/Integration";
import { createSortFunction } from "../../../Utils/TableUtils";
import { toLocalDateString } from "../../../Utils/DateUtils";
import { PaymentRoutes } from "../../../Contracts/Routes";
import { getPaymentMethodDetails } from "../../../Contracts/Transaction";
import { getCustomerCreationMethodLabel } from "../../../Contracts/Invoice";
import OpenInvoicesModal from "./OpenInvoicesModal";
import { ReactComponent as CloseIcon } from "../../../Assets/ActionIcons/CloseIcon.svg";
import IntegratedCustomerListFilter, {
  IntegratedCustomerListFilterValue
} from "./IntegratedCustomerListFilter";
import { ReactComponent as DropdownDownArrowIcon } from "../../../Assets/ActionIcons/DropdownDownArrowIcon.svg";
import { ReactComponent as ExclamationWarningIcon } from "../../../Assets/ActionIcons/ExclamationWarningIcon.svg";
import BulkActionsModal from "../../../Components/Molecules/BulkActionsModal";
import TableBulkActions from "../../../Components/Atoms/Table/TableBulkActions";
import IntegratedCustomerActions from "./IntegratedCustomerActions";

const DropDownActionProps: {
  label: string;
  value: string;
  getWarningMessage: (on: RowSelectionActionOn, rowsSelected: number, totalRows: number) => string;
  actionDescription: string;
}[] = [
  {
    label: "Request to add payment method",
    value: "request_to_add_payment_method",
    getWarningMessage: (on: RowSelectionActionOn, rowsSelected: number, totalRows: number) =>
      `Are you sure you want to send email ${
        on === "all" ? `all ${totalRows}` : rowsSelected
      } selected clients for adding authorization or mandate?`,
    actionDescription: "Sending email for adding authorization or mandate"
  },
  {
    label: "Enable Invoice Emails",
    value: "enable_invoice_emails",
    getWarningMessage: (on: RowSelectionActionOn, rowsSelected: number, totalRows: number) =>
      `Are you sure you want to enable ${
        on === "all" ? `all ${totalRows}` : rowsSelected
      } selected clients to receive invoice emails and reminders?`,
    actionDescription: "Enabling invoice emails and reminders"
  },
  {
    label: "Disable Invoice Emails",
    value: "disable_invoice_emails",
    getWarningMessage: (on: RowSelectionActionOn, rowsSelected: number, totalRows: number) =>
      `Are you sure you want to stop sending invoice emails and reminders to ${
        on === "all" ? `all ${totalRows}` : rowsSelected
      } selected clients? This action will also disable auto-collect if currently enabled.`,
    actionDescription: "Disabling invoice emails and reminders"
  },
  {
    label: "Enable Invoice Emails and Auto Collect",
    value: "enable_invoice_emails_and_auto_collect",
    getWarningMessage: (on: RowSelectionActionOn, rowsSelected: number, totalRows: number) =>
      `Are you sure you want to enable ${
        on === "all" ? `all ${totalRows}` : rowsSelected
      } selected clients for auto-collection and to receive invoice emails?`,
    actionDescription: "Enabling invoice emails and auto-collection"
  },
  {
    label: "Disable Invoice Emails and Auto Collect",
    value: "disable_invoice_emails_and_auto_collect",
    getWarningMessage: (on: RowSelectionActionOn, rowsSelected: number, totalRows: number) =>
      `Are you sure you want to disable ${
        on === "all" ? `all ${totalRows}` : rowsSelected
      } selected clients for auto-collection and to stop sending invoice emails?`,
    actionDescription: "Disabling invoice emails and auto-collection"
  }
];

interface Props {
  connectedIntegration: PaymentsIntegrationEnum;
}

const IntegratedCustomersList: React.FC<Props> = ({ connectedIntegration }) => {
  const [invoiceDetailModalDetails, setInvoiceDetailModalDetails] = useState<{
    open: boolean;
    selectedCustomer?: Customer;
  }>({ open: false });
  const [sortParamValue, setSortParamValue] = useState<string>("updated_at:desc");
  const { limit, page, setLimit, setPage } = usePagination(
    PAGINATION_KEYS.INTEGRATED_CUSTOMERS_LIST,
    { limit: 25, page: 1 }
  );
  const [bulkActionProps, setBulkActionProps] = useState<{
    isOpen: boolean;
    data: DataSourceItem<Customer>[];
    description?: string;
    title?: string;
    action?: string;
  }>({
    isOpen: false,
    data: []
  });
  const [selected, setSelected] = useState<{
    selectedKeys: string[];
    selectedRows: DataSourceItem<Customer>[];
  }>({
    selectedKeys: [],
    selectedRows: []
  });
  const [actionsOpen, setActionsOpen] = useState<string>("");

  const defaultIntegratedCustomerListFilterValue: IntegratedCustomerListFilterValue = {
    creationMethod: "all",
    createdDateStart: null,
    createdDateEnd: null
  };
  const [filterValue, setFilterValue] = useState<IntegratedCustomerListFilterValue>(
    defaultIntegratedCustomerListFilterValue
  );

  const { getAccessTokenSilently } = useAuth0();
  const { getCurrentTeam, currentTeam } = useTeamStore();

  const columns: Column<Customer>[] = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      sortClicked: createSortFunction("name", setSortParamValue),
      width: "w-96",
      render: d => (
        <a
          href={`${window.location.origin}${PaymentRoutes.CUSTOMERS}/${d.data.id}`}
          className="text-blue-600"
        >
          {d.data.name}
        </a>
      )
    },
    {
      title: "Invoice Emails",
      dataIndex: "emails",
      key: "emails",
      width: "w-64",
      sortClicked: createSortFunction("reminder_email_enabled_date", setSortParamValue),
      render: d => <Tag color={d.emails === "Disabled" ? "gray" : "lime"}>{d.emails}</Tag>
    },
    {
      title: "Auto Collect",
      dataIndex: "auto_collect",
      key: "auto_collect",
      width: "w-64",
      sortClicked: createSortFunction("auto_invoice_collection_enabled_date", setSortParamValue),
      render: d => (
        <Tag color={d.auto_collect === "Disabled" ? "gray" : "lime"}>{d.auto_collect}</Tag>
      )
    },
    ...(currentTeam?.early_access_features?.["customer-auto-mapping"]
      ? [
          {
            title: "Create Date",
            dataIndex: "created_at",
            key: "created_at",
            render: (d: DataSourceItem<Customer>) => (
              <span title={toLocalDateString(d.data.created_at, true)}>
                {toLocalDateString(d.data.created_at)}
              </span>
            ),
            sortClicked: createSortFunction("created_at", setSortParamValue)
          },
          {
            title: "Creation Method",
            dataIndex: "mapping_source",
            key: "mapping_source",
            render: (d: DataSourceItem<Customer>) => {
              const text = getCustomerCreationMethodLabel(
                d.data.origin,
                d.data.mapping_source,
                connectedIntegration
              );
              return <Tag color="blue">{text}</Tag>;
            }
          }
        ]
      : []),
    {
      title: "Preferred Payment method",
      dataIndex: "payment_method",
      key: "payment_method",
      render: d => {
        const imageDetails =
          d.data.preferred_payment_method_type &&
          getPaymentMethodDetails(d.data.preferred_payment_method_type);
        return (
          imageDetails && <img src={imageDetails.logo} alt={imageDetails.name} className="h-10" />
        );
      }
    },
    {
      title: "Open Invoices",
      dataIndex: "open_invoices",
      key: "open_invoices",
      width: "w-40",
      render: d => (
        <div className="flex space-x-2 items-center text-blue-600">
          <div
            className="cursor-pointer"
            onClick={() =>
              setInvoiceDetailModalDetails({
                open: true,
                selectedCustomer: d.data
              })
            }
          >
            {d.open_invoices}
          </div>
          {d.data.overdue_invoices_count && d.data.overdue_invoices_count > 0 && (
            <div
              className="group flex cursor-pointer"
              onClick={() =>
                setInvoiceDetailModalDetails({
                  open: true,
                  selectedCustomer: d.data
                })
              }
            >
              <ExclamationWarningIcon className="text-[#F5222D]" />
              <div className="relative">
                <div className="absolute top-0 left-1 text-red-600 text-xs group-hover:block hidden  transition-opacity delay-500">
                  {d.data.overdue_invoices_count} Overdue Invoice
                  {d.data.overdue_invoices_count > 1 && "s"}
                </div>
              </div>
            </div>
          )}
        </div>
      ),
      sortClicked: createSortFunction("open_invoices_count", setSortParamValue)
    },
    {
      title: "Actions",
      dataIndex: "actions",
      key: "actions",
      render: d => (
        <IntegratedCustomerActions
          row={d}
          open={actionsOpen === d.key}
          onOpen={() => setActionsOpen(d.key)}
          onClose={() => setActionsOpen("")}
          refreshList={refreshThings}
        />
      )
    }
  ];

  const [search, setSearch] = useState<string | undefined>(undefined);
  const [listIsLoading, setListIsLoading] = useState<boolean>(true);
  const debouncedSearch = useDebounce(search, 1500);
  const handleSearchInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.value.length > 0) {
      setSearch(event.target.value);
      setPage(1);
    } else {
      setSearch(undefined);
      setPage(1);
    }
  };
  const {
    customers,
    customersCount,
    getCustomers,
    editCustomerDetails,
    requestCustomerToAddPaymentMethod
  } = useCustomerStore();

  const { creationMethod, createdDateStart, createdDateEnd } = filterValue;
  let filterParams: string | undefined;
  if (creationMethod === "autoMapped") {
    filterParams = "mapping_source:eq:auto";
  } else if (creationMethod === "autoCreated") {
    filterParams = "origin:eq:zomentum_grow,mapping_source:eq:auto";
  } else if (creationMethod === "mappedManually") {
    filterParams = "mapping_source:eq:manual";
  } else if (creationMethod === "imported") {
    filterParams = "origin:ne:zomentum_grow";
  } else if (creationMethod === "fromZomentumGrow") {
    filterParams = "origin:eq:zomentum_grow";
  }
  if (createdDateStart) {
    const from = createdDateStart.toISOString().split("T")[0];
    filterParams = filterParams
      ? `${filterParams};created_at:gte:${from}`
      : `created_at:gte:${from}`;
  }
  if (createdDateEnd) {
    const to = createdDateEnd.toISOString().split("T")[0];
    filterParams = filterParams ? `${filterParams};created_at:lte:${to}` : `created_at:lte:${to}`;
  }

  const refreshThings = useCallback(async () => {
    setListIsLoading(true);
    await getCustomers(
      getAccessTokenSilently,
      limit,
      sortParamValue,
      page,
      filterParams,
      debouncedSearch
    );
    setListIsLoading(false);
  }, [
    debouncedSearch,
    filterParams,
    getCustomers,
    getAccessTokenSilently,
    limit,
    sortParamValue,
    page
  ]);
  useEffect(() => {
    getCurrentTeam(getAccessTokenSilently);
    refreshThings();
  }, [
    debouncedSearch,
    refreshThings,
    limit,
    sortParamValue,
    page,
    getCurrentTeam,
    getAccessTokenSilently
  ]);

  const onRowSelectionAction = (selectedValue: string | number, on: RowSelectionActionOn) => {
    const targetValue = DropDownActionProps.find(option => option.value === selectedValue);
    if (targetValue) {
      if (
        window.confirm(
          targetValue.getWarningMessage(on, selected.selectedKeys.length, customersCount || 0)
        )
      ) {
        setBulkActionProps({
          isOpen: true,
          data: selected.selectedRows || [],
          description: targetValue.actionDescription,
          title: targetValue.label,
          action: targetValue.value
        });
      }
    } else {
      window.alert("Something went wrong");
    }
  };

  const onDataAction = async (data: DataSourceItem<Customer>) => {
    switch (bulkActionProps.action) {
      case "request_to_add_payment_method":
        return await requestCustomerToAddPaymentMethod(
          getAccessTokenSilently,
          data.data.id.toString()
        );
      case "enable_invoice_emails":
        return await editCustomerDetails(getAccessTokenSilently, data.data.id.toString(), {
          reminder_enabled_date: new Date()
        });
      case "disable_invoice_emails":
        return await editCustomerDetails(getAccessTokenSilently, data.data.id.toString(), {
          reminder_enabled_date: null
        });
      case "enable_invoice_emails_and_auto_collect":
        return await editCustomerDetails(getAccessTokenSilently, data.data.id.toString(), {
          reminder_enabled_date: new Date(),
          auto_collection_enabled_date: new Date()
        });
      case "disable_invoice_emails_and_auto_collect":
        return await editCustomerDetails(getAccessTokenSilently, data.data.id.toString(), {
          reminder_enabled_date: null,
          auto_collection_enabled_date: null
        });
      default:
        return Promise.reject("Invalid action");
    }
  };

  const dataSource: DataSourceItem<Customer>[] = customers.map(customer => ({
    key: customer.id.toString(),
    emails: customer.reminder_enabled_date ? "Enabled" : "Disabled",
    auto_collect: customer.auto_collection_enabled_date ? "Enabled" : "Disabled",
    payment_method: customer.preferred_payment_method_type || "",
    open_invoices: customer.open_invoices_count || 0,
    data: customer
  }));

  return (
    <>
      <OpenInvoicesModal
        open={invoiceDetailModalDetails.open}
        setInvoiceDetailModalDetails={setInvoiceDetailModalDetails}
        selectedCustomer={invoiceDetailModalDetails.selectedCustomer}
      />
      <BulkActionsModal<Customer>
        {...bulkActionProps}
        dataValueKey="name"
        size="medium"
        topOffset="10rem"
        onClose={() => {
          setBulkActionProps({ isOpen: false, data: [], action: undefined });
          setSelected({ selectedKeys: [], selectedRows: [] });
        }}
        onDataAction={onDataAction}
        onAllActionsComplete={refreshThings}
      />
      {selected.selectedKeys.length > 0 ? (
        <div className="mt-[0.6rem] z-20">
          <TableBulkActions
            selectItemsLength={selected.selectedKeys.length}
            dataSourceLength={dataSource.length}
            clearSelections={() => setSelected({ selectedKeys: [], selectedRows: [] })}
            totalRows={customersCount}
            onRowSelectionAction={onRowSelectionAction}
            actionsDropdownProps={{
              options: DropDownActionProps.map(option => ({
                label: option.label,
                value: option.value
              })),
              anchor: (
                <div className="w-full px-2 py-1 rounded border flex items-center space-x-4 text-gray-600">
                  <div>Actions</div>
                  <DropdownDownArrowIcon className="w-3 h-3" />
                </div>
              ),
              position: "bottom-right"
            }}
          />
        </div>
      ) : (
        <div className="flex items-center space-x-2 mt-2">
          <input
            placeholder="Search for a customer.."
            value={search || ""}
            onChange={handleSearchInputChange}
            className="border border-gray-300 rounded p-1 my-1 w-48"
          />
          {search && (
            <CloseIcon
              className="h-3 w-3 hover:scale-110 transition-all duration-500 hover:text-red-600 cursor-pointer"
              onClick={() => setSearch(undefined)}
            />
          )}
          {currentTeam?.early_access_features?.["customer-auto-mapping"] && (
            <IntegratedCustomerListFilter value={filterValue} onChange={setFilterValue} />
          )}
        </div>
      )}
      <div className="flex flex-grow flex-col overflow-y-auto">
        {listIsLoading && customers.length === 0 ? (
          <LoadingPage />
        ) : (
          <Table
            dataSource={dataSource}
            columns={columns}
            isLoading={listIsLoading}
            rowSelectionOptions={{
              enableSelection: true,
              selected: selected.selectedKeys,
              onRowSelectionChange: (keys: string[], rows: DataSourceItem<Customer>[]) => {
                setSelected({
                  selectedKeys: keys,
                  selectedRows: rows
                });
              }
            }}
          />
        )}
        {!listIsLoading && customers.length === 0 && (
          <div className="text-center text-gray-400 mt-12">No customers found</div>
        )}
      </div>
      <TablePaginationFooter
        limit={limit}
        setLimit={setLimit}
        totalRecords={customersCount}
        page={page}
        setPage={setPage}
        isLoading={listIsLoading}
      />
    </>
  );
};

export default IntegratedCustomersList;
